import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api-client';
import { 
  DollarSign, Save, Plus, Trash2, Check, X, RefreshCw, 
  ToggleLeft, ToggleRight, Star, Edit2, Clock, Shield
} from 'lucide-react';

export default function AdminPricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [trialConfig, setTrialConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');

  const isSuperuser = user?.is_superuser || user?.is_admin || user?.is_staff;

  useEffect(() => {
    if (!isSuperuser) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [isSuperuser, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansData, trialData] = await Promise.all([
        api.getAdminPlans(),
        api.getTrialConfig()
      ]);
      setPlans(plansData);
      setTrialConfig(trialData);
    } catch (err) {
      console.error('Failed to load pricing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePlan = async (planId, updates) => {
    setSaving(true);
    try {
      const updated = await api.updatePlan(planId, updates);
      setPlans(prev => prev.map(p => p.id === planId ? updated : p));
      setEditingPlan(null);
    } catch (err) {
      console.error('Failed to update plan:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this pricing plan?')) return;
    try {
      await api.deletePlan(planId);
      setPlans(prev => prev.filter(p => p.id !== planId));
    } catch (err) {
      console.error('Failed to delete plan:', err);
    }
  };

  const handleCreatePlan = async (planData) => {
    setSaving(true);
    try {
      const created = await api.createPlan(planData);
      setPlans(prev => [...prev, created]);
      setShowAddPlan(false);
    } catch (err) {
      console.error('Failed to create plan:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTrialConfig = async (updates) => {
    setSaving(true);
    try {
      const updated = await api.updateTrialConfig(updates);
      setTrialConfig(updated);
    } catch (err) {
      console.error('Failed to update trial config:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperuser) return null;

  return (
    <div data-testid="admin-pricing" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="text-emerald-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-white font-mono">Pricing & Trials</h1>
            <p className="text-sm text-zinc-500">Manage pricing plans and free trial settings</p>
          </div>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-300 rounded-sm hover:bg-zinc-700 transition-all font-mono text-sm"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2">
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-4 py-2 text-sm font-mono rounded-t-sm transition-all ${
            activeTab === 'plans' 
              ? 'bg-emerald-500/10 text-emerald-400 border-b-2 border-emerald-500' 
              : 'text-zinc-500 hover:text-white'
          }`}
        >
          Pricing Plans
        </button>
        <button
          onClick={() => setActiveTab('trial')}
          className={`px-4 py-2 text-sm font-mono rounded-t-sm transition-all ${
            activeTab === 'trial' 
              ? 'bg-emerald-500/10 text-emerald-400 border-b-2 border-emerald-500' 
              : 'text-zinc-500 hover:text-white'
          }`}
        >
          Free Trial Settings
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6 animate-pulse">
              <div className="h-6 bg-zinc-800 rounded w-1/4 mb-4"></div>
              <div className="h-4 bg-zinc-800 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : activeTab === 'plans' ? (
        <PricingPlansTab 
          plans={plans}
          editingPlan={editingPlan}
          setEditingPlan={setEditingPlan}
          onUpdatePlan={handleUpdatePlan}
          onDeletePlan={handleDeletePlan}
          onCreatePlan={handleCreatePlan}
          showAddPlan={showAddPlan}
          setShowAddPlan={setShowAddPlan}
          saving={saving}
        />
      ) : (
        <FreeTrialTab 
          config={trialConfig}
          onUpdate={handleUpdateTrialConfig}
          saving={saving}
          plans={plans}
        />
      )}
    </div>
  );
}

// Pricing Plans Tab Component
function PricingPlansTab({ 
  plans, editingPlan, setEditingPlan, onUpdatePlan, onDeletePlan, 
  onCreatePlan, showAddPlan, setShowAddPlan, saving 
}) {
  const [newPlan, setNewPlan] = useState({
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    price_annual: 0,
    features: [],
    max_agents: 1,
    vcpu: 2,
    ram_gb: 4,
    storage_gb: 50,
    max_skills: 5,
    is_active: true,
    is_highlighted: false,
    sort_order: plans.length + 1
  });
  const [featuresText, setFeaturesText] = useState('');

  return (
    <div className="space-y-4">
      {/* Add Plan Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddPlan(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-black rounded-sm hover:bg-emerald-400 transition-all font-mono text-sm font-medium"
        >
          <Plus size={14} />
          Add Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {plans.map(plan => (
          <div 
            key={plan.id}
            className={`bg-[#0A0A0A] border rounded-sm p-6 ${
              plan.is_highlighted ? 'border-emerald-500/50' : 'border-zinc-800'
            } ${!plan.is_active ? 'opacity-60' : ''}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-white font-mono">{plan.display_name}</h3>
                  {plan.is_highlighted && <Star size={14} className="text-emerald-400 fill-emerald-400" />}
                  {!plan.is_active && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">Inactive</span>}
                </div>
                <p className="text-xs text-zinc-500 font-mono">{plan.name}</p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setEditingPlan(plan)}
                  className="p-2 text-zinc-500 hover:text-emerald-400 transition-all"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => onDeletePlan(plan.id)}
                  className="p-2 text-zinc-500 hover:text-red-400 transition-all"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <p className="text-sm text-zinc-400 mb-4">{plan.description}</p>

            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-3xl font-bold text-white font-mono">${plan.price_monthly}</span>
              <span className="text-zinc-500 text-sm">/mo</span>
              <span className="text-zinc-600 text-xs ml-2">(${plan.price_annual}/mo annual)</span>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
              <div className="bg-zinc-900 p-2 rounded">
                <span className="text-zinc-500">Agents:</span>
                <span className="text-white ml-1">{plan.max_agents}</span>
              </div>
              <div className="bg-zinc-900 p-2 rounded">
                <span className="text-zinc-500">Skills:</span>
                <span className="text-white ml-1">{plan.max_skills === -1 ? '∞' : plan.max_skills}</span>
              </div>
              <div className="bg-zinc-900 p-2 rounded">
                <span className="text-zinc-500">vCPU:</span>
                <span className="text-white ml-1">{plan.vcpu}</span>
              </div>
              <div className="bg-zinc-900 p-2 rounded">
                <span className="text-zinc-500">RAM:</span>
                <span className="text-white ml-1">{plan.ram_gb}GB</span>
              </div>
            </div>

            <div className="border-t border-zinc-800 pt-4">
              <p className="text-xs text-zinc-500 mb-2">Features ({plan.features?.length || 0})</p>
              <div className="flex flex-wrap gap-1">
                {plan.features?.slice(0, 4).map((f, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-zinc-800 text-zinc-400 rounded">{f}</span>
                ))}
                {plan.features?.length > 4 && (
                  <span className="text-xs px-2 py-1 bg-zinc-800 text-zinc-500 rounded">+{plan.features.length - 4} more</span>
                )}
              </div>
            </div>

            {/* Quick Toggle Actions */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-800">
              <button
                onClick={() => onUpdatePlan(plan.id, { is_active: !plan.is_active })}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-mono rounded transition-all ${
                  plan.is_active 
                    ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                    : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                }`}
              >
                {plan.is_active ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                {plan.is_active ? 'Active' : 'Inactive'}
              </button>
              <button
                onClick={() => onUpdatePlan(plan.id, { is_highlighted: !plan.is_highlighted })}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-mono rounded transition-all ${
                  plan.is_highlighted 
                    ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' 
                    : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                }`}
              >
                <Star size={12} className={plan.is_highlighted ? 'fill-amber-400' : ''} />
                Popular
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Plan Modal */}
      {editingPlan && (
        <PlanEditModal
          plan={editingPlan}
          onSave={(updates) => onUpdatePlan(editingPlan.id, updates)}
          onClose={() => setEditingPlan(null)}
          saving={saving}
        />
      )}

      {/* Add Plan Modal */}
      {showAddPlan && (
        <PlanEditModal
          plan={newPlan}
          onSave={onCreatePlan}
          onClose={() => setShowAddPlan(false)}
          saving={saving}
          isNew
        />
      )}
    </div>
  );
}

// Plan Edit Modal
function PlanEditModal({ plan, onSave, onClose, saving, isNew = false }) {
  const [formData, setFormData] = useState({
    ...plan,
    features: plan.features || []
  });
  const [featuresText, setFeaturesText] = useState((plan.features || []).join('\n'));

  const handleSave = () => {
    const features = featuresText.split('\n').filter(f => f.trim());
    onSave({ ...formData, features });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800">
          <h3 className="text-lg font-bold text-white font-mono">
            {isNew ? 'Create New Plan' : `Edit ${plan.display_name}`}
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Plan Name (slug)</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                disabled={!isNew}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none disabled:opacity-50"
                placeholder="enterprise_plus"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
                placeholder="Enterprise Plus"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Monthly Price ($)</label>
              <input
                type="number"
                value={formData.price_monthly}
                onChange={(e) => setFormData({...formData, price_monthly: parseFloat(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Annual Price ($/mo)</label>
              <input
                type="number"
                value={formData.price_annual}
                onChange={(e) => setFormData({...formData, price_annual: parseFloat(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Max Agents</label>
              <input
                type="number"
                value={formData.max_agents}
                onChange={(e) => setFormData({...formData, max_agents: parseInt(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">vCPU</label>
              <input
                type="number"
                value={formData.vcpu}
                onChange={(e) => setFormData({...formData, vcpu: parseInt(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">RAM (GB)</label>
              <input
                type="number"
                value={formData.ram_gb}
                onChange={(e) => setFormData({...formData, ram_gb: parseInt(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Storage (GB)</label>
              <input
                type="number"
                value={formData.storage_gb}
                onChange={(e) => setFormData({...formData, storage_gb: parseInt(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Max Skills (-1 for unlimited)</label>
              <input
                type="number"
                value={formData.max_skills}
                onChange={(e) => setFormData({...formData, max_skills: parseInt(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1">Features (one per line)</label>
            <textarea
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
              rows={6}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none resize-none"
              placeholder="1 Agent&#10;2 vCPU&#10;4GB RAM&#10;..."
            />
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-emerald-500"
              />
              <span className="text-sm text-zinc-400">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_highlighted}
                onChange={(e) => setFormData({...formData, is_highlighted: e.target.checked})}
                className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-emerald-500"
              />
              <span className="text-sm text-zinc-400">Highlighted (Most Popular)</span>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-800 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-sm hover:bg-zinc-700 transition-all font-mono text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !formData.name}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-black rounded-sm hover:bg-emerald-400 transition-all font-mono text-sm font-medium disabled:opacity-50"
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            {isNew ? 'Create Plan' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Free Trial Tab Component
function FreeTrialTab({ config, onUpdate, saving, plans }) {
  const [formData, setFormData] = useState(config || {});

  useEffect(() => {
    if (config) setFormData(config);
  }, [config]);

  const handleToggle = (field) => {
    const newValue = !formData[field];
    setFormData(prev => ({ ...prev, [field]: newValue }));
    onUpdate({ [field]: newValue });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onUpdate(formData);
  };

  if (!config) return null;

  return (
    <div className="space-y-6">
      {/* Main Toggle */}
      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-sm ${config.is_enabled ? 'bg-emerald-500/20' : 'bg-zinc-800'}`}>
              <Clock size={24} className={config.is_enabled ? 'text-emerald-400' : 'text-zinc-500'} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white font-mono">Free Trial</h3>
              <p className="text-sm text-zinc-500">
                {config.is_enabled 
                  ? `${config.trial_days}-day free trial is active` 
                  : 'Free trial is currently disabled'}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle('is_enabled')}
            className={`flex items-center gap-2 px-6 py-3 rounded-sm font-mono text-sm font-medium transition-all ${
              config.is_enabled 
                ? 'bg-emerald-500 text-black hover:bg-emerald-400' 
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            {config.is_enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
            {config.is_enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      {/* Trial Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6 space-y-4">
          <h4 className="text-white font-mono font-medium flex items-center gap-2">
            <Clock size={16} className="text-emerald-400" />
            Trial Duration & Plan
          </h4>
          
          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1">Trial Duration (days)</label>
            <input
              type="number"
              value={formData.trial_days}
              onChange={(e) => handleChange('trial_days', parseInt(e.target.value))}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1">Trial Plan Features</label>
            <select
              value={formData.trial_plan}
              onChange={(e) => handleChange('trial_plan', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
            >
              {plans.map(plan => (
                <option key={plan.name} value={plan.name}>{plan.display_name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Max Agents (Trial)</label>
              <input
                type="number"
                value={formData.trial_max_agents}
                onChange={(e) => handleChange('trial_max_agents', parseInt(e.target.value))}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1">Max Skills (Trial)</label>
              <input
                type="number"
                value={formData.trial_max_skills}
                onChange={(e) => handleChange('trial_max_skills', parseInt(e.target.value))}
                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1">Action After Trial Ends</label>
            <select
              value={formData.trial_end_action}
              onChange={(e) => handleChange('trial_end_action', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
            >
              <option value="pause">Pause Instance</option>
              <option value="downgrade">Downgrade to Free</option>
              <option value="delete">Delete Instance</option>
            </select>
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6 space-y-4">
          <h4 className="text-white font-mono font-medium flex items-center gap-2">
            <Shield size={16} className="text-emerald-400" />
            Requirements & UI Text
          </h4>
          
          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm cursor-pointer">
              <span className="text-sm text-zinc-400">Require Email Verification</span>
              <button
                onClick={() => handleToggle('require_email_verification')}
                className={`p-1 rounded ${formData.require_email_verification ? 'text-emerald-400' : 'text-zinc-600'}`}
              >
                {formData.require_email_verification ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              </button>
            </label>

            <label className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm cursor-pointer">
              <span className="text-sm text-zinc-400">Require Payment Method</span>
              <button
                onClick={() => handleToggle('require_payment_method')}
                className={`p-1 rounded ${formData.require_payment_method ? 'text-emerald-400' : 'text-zinc-600'}`}
              >
                {formData.require_payment_method ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              </button>
            </label>
          </div>

          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1">CTA Button Text</label>
            <input
              type="text"
              value={formData.trial_cta_text}
              onChange={(e) => handleChange('trial_cta_text', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              placeholder="Start Free Trial"
            />
          </div>

          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1">Trial Badge Text</label>
            <input
              type="text"
              value={formData.trial_badge_text}
              onChange={(e) => handleChange('trial_badge_text', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-emerald-500 outline-none"
              placeholder="14-day free trial"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-500 text-black rounded-sm hover:bg-emerald-400 transition-all font-mono text-sm font-medium disabled:opacity-50"
        >
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
          Save Trial Settings
        </button>
      </div>
    </div>
  );
}
